import type { CharacterState, Rules } from "@/data/types";
import { calcDerivedStats } from "@/lib/characterCalc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Heart,
  Zap,
  Coins,
  Plus,
  Minus,
  Check,
  AlertTriangle,
  Lock,
  Shuffle,
} from "lucide-react";

export function StepCoreSkills({
  state,
  rules,
  stats,
  onChange,
}: {
  state: CharacterState;
  rules: Rules;
  stats: ReturnType<typeof calcDerivedStats>;
  onChange: (s: CharacterState) => void;
}) {
  const remaining = stats.totalSkillPoints - stats.usedSkillPoints;
  const { maxHp, maxWealthFromSkills } = rules.limits;

  const getCount = (skillId: string) =>
    state.coreSkillSelections.find((s) => s.skillId === skillId)?.count ?? 0;

  const setCount = (skillId: string, count: number) => {
    const others = state.coreSkillSelections.filter(
      (s) => s.skillId !== skillId
    );
    const newSelections = count > 0 ? [...others, { skillId, count }] : others;
    onChange({ ...state, coreSkillSelections: newSelections });
  };

  const randomiseAll = () => {
    const claimedByGift = new Set(
      [...state.pacts, ...(state.bonusPacts || [])]
        .filter(
          (p) => p.giftId === "gift-of-earthly-experience" && p.giftSkillId
        )
        .map((p) => p.giftSkillId!)
    );

    const counts: Record<string, number> = {};
    let simHp = 3; // BASE_HP
    let simWealth = 1; // BASE_WEALTH
    const budget = stats.totalSkillPoints;

    for (let placed = 0; placed < budget; placed++) {
      const eligible = rules.coreSkills.filter((skill) => {
        const count = counts[skill.id] ?? 0;
        if (!skill.repeatable && count > 0) return false;
        if (claimedByGift.has(skill.id)) return false;
        if (skill.maxTimes !== undefined && count >= skill.maxTimes)
          return false;
        if (skill.id === "endurance-training") return simHp < maxHp;
        if (skill.id === "inherited-wealth")
          return simWealth < maxWealthFromSkills;
        return true;
      });
      if (eligible.length === 0) break;
      const pick = eligible[Math.floor(Math.random() * eligible.length)];
      counts[pick.id] = (counts[pick.id] ?? 0) + 1;
      if (pick.id === "endurance-training") simHp = Math.min(simHp + 2, maxHp);
      if (pick.id === "inherited-wealth")
        simWealth = Math.min(simWealth + 1, maxWealthFromSkills);
    }

    const newSelections = Object.entries(counts).map(([skillId, count]) => ({
      skillId,
      count,
    }));
    onChange({ ...state, coreSkillSelections: newSelections });
  };

  const canAdd = (skillId: string) => {
    if (remaining <= 0) return false;
    const skill = rules.coreSkills.find((s) => s.id === skillId);
    if (!skill) return false;
    if (!skill.repeatable) {
      if (getCount(skillId) > 0) return false;
      // Block if this non-repeatable skill is already claimed via a Gift of Earthly Experience
      const claimedByGift = [...state.pacts, ...(state.bonusPacts || [])].some(
        (p) =>
          p.giftId === "gift-of-earthly-experience" && p.giftSkillId === skillId
      );
      if (claimedByGift) return false;
    }
    if (skill.maxTimes !== undefined && getCount(skillId) >= skill.maxTimes)
      return false;
    if (skillId === "endurance-training") return stats.hp < maxHp;
    if (skillId === "inherited-wealth")
      return stats.wealthLevel < maxWealthFromSkills;
    return true;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold mb-1">Core Skills</h2>
          <p className="text-muted-foreground text-sm">
            Choose skills to define your character&apos;s abilities.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={randomiseAll}
            disabled={stats.totalSkillPoints === 0}
          >
            <Shuffle className="h-4 w-4 mr-1.5" />
            Randomise
          </Button>
          <div className="text-right">
            <div
              className={`text-lg font-bold ${remaining < 0 ? "text-destructive" : remaining === 0 ? "text-muted-foreground" : "text-primary"}`}
            >
              {remaining} / {stats.totalSkillPoints}
            </div>
            <div className="text-xs text-muted-foreground">
              points remaining
            </div>
          </div>
        </div>
      </div>

      {/* Derived preview */}
      <div className="flex gap-3 flex-wrap">
        <div className="flex items-center gap-1 text-sm">
          <Heart className="h-4 w-4 text-red-500" />
          <span className="font-medium">{stats.hp} HP</span>
        </div>
        <div className="flex items-center gap-1 text-sm">
          <Zap className="h-4 w-4 text-blue-500" />
          <span className="font-medium">{stats.mp} MP</span>
        </div>
        <div className="flex items-center gap-1 text-sm">
          <Coins className="h-4 w-4 text-yellow-500" />
          <span className="font-medium">
            {rules.wealthLevels[stats.wealthLevel]?.name ?? "Unknown"}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {rules.coreSkills.map((skill) => {
          const count = getCount(skill.id);
          const selected = count > 0;
          const claimedByGift =
            !skill.repeatable &&
            [...state.pacts, ...(state.bonusPacts || [])].some(
              (p) =>
                p.giftId === "gift-of-earthly-experience" &&
                p.giftSkillId === skill.id
            );
          const addable = canAdd(skill.id);
          const atHpCap =
            skill.id === "endurance-training" && stats.hp >= maxHp;
          const atWealthCap =
            skill.id === "inherited-wealth" &&
            stats.wealthLevel >= maxWealthFromSkills;

          return (
            <Card
              key={skill.id}
              className={`transition-colors ${selected ? "border-primary/50 bg-primary/5" : claimedByGift ? "opacity-60" : ""}`}
            >
              <CardContent className="pt-4 pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{skill.name}</span>
                      {skill.repeatable && (
                        <Badge variant="outline" className="text-xs">
                          Repeatable
                        </Badge>
                      )}
                      {skill.maxTimes && (
                        <Badge variant="secondary" className="text-xs">
                          Max {skill.maxTimes}×
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">
                        1 point
                      </Badge>
                      {claimedByGift && (
                        <Badge
                          variant="outline"
                          className="text-xs gap-1 text-muted-foreground"
                        >
                          <Lock className="h-2.5 w-2.5" />
                          Taken by gift
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {skill.description}
                    </p>
                    {atHpCap && (
                      <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        HP is at maximum ({maxHp})
                      </p>
                    )}
                    {atWealthCap && (
                      <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Wealth is at maximum (
                        {rules.wealthLevels[maxWealthFromSkills]?.name})
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {skill.repeatable && selected ? (
                      <>
                        <Button
                          variant="outline"
                          size="icon-sm"
                          onClick={() => setCount(skill.id, count - 1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-6 text-center text-sm font-medium">
                          {count}
                        </span>
                        <Button
                          variant="outline"
                          size="icon-sm"
                          onClick={() => setCount(skill.id, count + 1)}
                          disabled={!addable}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </>
                    ) : selected ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setCount(skill.id, 0)}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Selected
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => setCount(skill.id, 1)}
                        disabled={!addable}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Select
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
